import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import BulletRow from "@/components/kit/BulletRow";
import OriginalPanel from "@/components/kit/OriginalPanel";
import { CopyButton, Field, Section } from "@/components/kit/KitFields";
import { api, errMsg } from "@/lib/api";

export default function RoleBullets({ jobId, role, items, show, kit, onData, orig, side }) {
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(null);
  const rid = role.role_id;
  const tid = `role-${rid}`;
  const call = async (fn) => { try { const { data } = await fn(); onData(data); return data; } catch (e) { toast.error(errMsg(e)); return null; } };
  const setShow = (n) => call(() => api.put(`/jobs/${jobId}/kit/roles/${rid}`, { show: n }));
  const act = {
    move: (i, d) => { const ids = items.map((x) => x.id); [ids[i], ids[i + d]] = [ids[i + d], ids[i]]; return call(() => api.put(`/jobs/${jobId}/kit/roles/${rid}`, { order: ids })); },
    edit: (id, text) => call(() => api.patch(`/jobs/${jobId}/kit/items/${id}`, { text })),
    remove: (id) => call(() => api.delete(`/jobs/${jobId}/kit/items/${id}`)),
  };
  const more = async () => {
    setBusy(true);
    const d = await call(() => api.post(`/jobs/${jobId}/kit/roles/${rid}/more`));
    if (d) toast.success("1 new bullet added, ranked into the list");
    setBusy(false);
  };
  const picked = items.slice(0, show).filter((i) => !i.issues.length);
  const qbtn = (n, label) => (
    <button key={label} data-testid={`${tid}-show-${label}`} onClick={() => setShow(n)} disabled={n > items.length && label !== "all"}
      className={`text-xs px-2.5 py-1 rounded-md border transition-colors disabled:opacity-30 ${show === n ? "bg-blue-600 border-blue-500 text-white" : "border-border text-slate-300 hover:border-blue-500/60"}`}>
      {label === "all" ? `All ${items.length}` : `Top ${n}`}
    </button>
  );
  return (
    <Section testid={tid} title={`${role.title} · ${role.company}`} hint={`${role.start} – ${role.end}`}
      action={<CopyButton text={picked.map((i) => i.text).join("\n")} k={`ball:${rid}:${picked.map((i) => i.id).join()}`} kit={kit} testid={`${tid}-copy-all`} label="Copy picked bullets" />}>
      <div className="grid md:grid-cols-2 gap-x-6">
        <Field label="Job title" value={role.title} k={`r:${rid}:title`} kit={kit} testid={`${tid}-title`} />
        <Field label="Company" value={role.company} k={`r:${rid}:company`} kit={kit} testid={`${tid}-company`} />
        <Field label="Location" value={role.location} k={`r:${rid}:loc`} kit={kit} testid={`${tid}-location`} />
        <Field label="Start date" value={role.start} k={`r:${rid}:start`} kit={kit} testid={`${tid}-start`} />
        <Field label="End date" value={role.end} k={`r:${rid}:end`} kit={kit} testid={`${tid}-end`} />
        {role.company_descriptor && <Field label="Company note" value={role.company_descriptor} k={`r:${rid}:desc`} kit={kit} testid={`${tid}-descriptor`} />}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4 mb-2">
        <div className="label-xs mr-1">Bullets on the resume</div>
        {[qbtn(3, "3"), qbtn(4, "4"), qbtn(5, "5"), items.length > 5 && qbtn(items.length, "all")]}
        <div className="ml-auto text-xs text-slate-400" data-testid={`${tid}-picked-count`}>{picked.length} picked of {items.length}</div>
      </div>
      <div className={side ? "grid lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 items-start" : ""}>
      {side && <div className="lg:sticky lg:top-[130px]"><OriginalPanel orig={orig} role={role} items={items} show={show} hover={hover} setHover={setHover} tid={tid} /></div>}
      <div className="space-y-2">
        {items.length > 0 && items.length < 5 && <div className="text-xs text-slate-400" data-testid={`${tid}-few-points`}>The original resume has {items.length} distinct point{items.length > 1 ? "s" : ""} for this job, and each one is used once. Generate more writes a new one from the client's facts.</div>}
        {items.map((it, i) => <BulletRow key={it.id} it={it} idx={i} total={items.length} picked={i < show} kit={kit} act={act}
          lit={side && hover && it.origin?.key === hover} onHover={side ? setHover : null} />)}
        {!items.length && <div className="text-sm text-slate-500">No bullets yet for this role.</div>}
      </div>
      </div>
      <button data-testid={`${tid}-generate-more`} onClick={more} disabled={busy}
        className="mt-3 inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-dashed border-blue-500/50 text-blue-300 hover:bg-blue-500/10 transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}{busy ? "Writing from the client's facts…" : "Generate 1 more bullet"}
      </button>
    </Section>
  );
}
